import { useContext, useState, useCallback } from 'react';
import { HimmaContext } from '../Context/HimmaContext';
import {
  AddMealRequest,
  EditMealRequest,
} from '../client-sdk/api';
import axios from 'axios';

/**
 * Get base URL from environment
 */
const getBaseUrl = () => {
  const env = import.meta.env as Record<string, string>;
  return env.VITE_BASE_URL || 'http://localhost:3001';
};

/**
 * Get auth token from localStorage
 */
const getAuthHeaders = () => {
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  const token = userData?.token;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Custom hook for Meal API operations
 */
export const useMealApi = () => {
  const api = useContext(HimmaContext)!;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Create a new meal
   */
  const createMeal = useCallback(
    async (mealData: AddMealRequest) => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.addMeal(mealData);
        return response.data;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'An error occurred';
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  /**
   * Edit existing meal
   */
  const editMeal = useCallback(
    async (id: string, mealData: EditMealRequest) => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.editMeal(id, mealData);
        return response.data;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'An error occurred';
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  /**
   * Get all meals with pagination and filters
   * Using direct axios call until SDK is regenerated
   */
  const getAllMeals = useCallback(
    async (query: any) => {
      setLoading(true);
      setError(null);
      try {
        const baseUrl = getBaseUrl();
        const response = await axios.get(`${baseUrl}/core/meal/getAll`, {
          params: {
            page: query.page,
            limit: query.limit,
            search: query.search,
            sortBy: query.sortBy,
            sortOrder: query.sortOrder,
            storeId: query.storeId,
            storeType: query.storeType,
            status: query.status,
          },
          headers: getAuthHeaders(),
        });
        return response.data;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'An error occurred';
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Get meal by ID
   * Using direct axios call
   */
  const getMealById = useCallback(
    async (id: string) => {
      setLoading(true);
      setError(null);
      try {
        const baseUrl = getBaseUrl();
        const response = await axios.get(
          `${baseUrl}/core/meal/${id}`,
          { headers: getAuthHeaders() }
        );
        return response.data;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'An error occurred';
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Toggle meal status (block/unblock)
   * Using direct axios call
   */
  const getToggleStatus = useCallback(
    async (query: any) => {
      setLoading(true);
      setError(null);
      try {
        const baseUrl = getBaseUrl();
        const response = await axios.patch(
          `${baseUrl}/core/meal/toggleStatus`,
          { id: query.id, status: query.status },
          { headers: getAuthHeaders() }
        );
        return response.data;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'An error occurred';
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Delete meal(s)
   * Using direct axios call
   */
  const deleteMeal = useCallback(
    async (ids: string[]) => {
      setLoading(true);
      setError(null);
      try {
        const baseUrl = getBaseUrl();
        const response = await axios.post(
          `${baseUrl}/core/meal/delete`,
          { mealIds: ids },
          { headers: getAuthHeaders() }
        );
        return response.data;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'An error occurred';
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return {
    createMeal,
    editMeal,
    getAllMeals,
    getMealById,
    getToggleStatus,
    deleteMeal,
    loading,
    error,
  };
};